// Explicit .ts extensions: this module is unit-tested by Node directly, and
// Node's type stripping will not resolve an extensionless path.
import { TIDE_STATIONS } from '../tideStations.ts';
import { CURRENT_STATIONS } from '../currentStations.ts';

/**
 * The national CO-OPS tide and current stations.
 *
 * Positions only, and that is the whole design. CO-OPS has no bulk endpoint and
 * rejects multi-station queries, so unlike the NDBC buoys — where one file
 * carries the country — a value for every visible marker would be one request
 * per marker. These are placed from a shipped registry and fetched only when one
 * is opened, which costs a single request for the station the reader chose.
 *
 * The registries are loaded through a dynamic import so they become their own
 * chunk: 74 KB gzipped for the tide list is worth nothing to a reader who never
 * turns the layer on, and the Bay's own stations are already in the main bundle.
 */

export interface CoopsPoint {
  id: string;
  name: string;
  lat: number;
  lon: number;
  /**
   * Tide stations only: NOAA publishes this one's turns but no harmonic curve.
   * They outnumber the reference stations nearly two to one, so they are hidden
   * behind their own toggle rather than crowding the coast by default.
   */
  subordinate?: boolean;
}

export interface Bounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

let tideCache: CoopsPoint[] | null = null;
let currentCache: CoopsPoint[] | null = null;
let tideInFlight: Promise<CoopsPoint[]> | null = null;
let currentInFlight: Promise<CoopsPoint[]> | null = null;

/**
 * Excludes anything the curated set already covers.
 *
 * The generator does this too, against the curated lists as they stood when it
 * ran. Repeating it here means adding a Bay station does not silently produce a
 * duplicate marker until someone remembers to regenerate.
 */
function withoutCurated(rows: CoopsPoint[], curated: ReadonlyArray<{ id: string }>): CoopsPoint[] {
  const ids = new Set(curated.map((c) => c.id));
  return rows.filter((r) => !ids.has(r.id));
}

/** The national tide stations, loaded once. */
export function nationalTideStations(): Promise<CoopsPoint[]> {
  if (tideCache) return Promise.resolve(tideCache);
  if (tideInFlight) return tideInFlight;

  tideInFlight = import('../nationalTideStations.ts')
    .then((m) => {
      tideCache = withoutCurated(
        [...m.NATIONAL_TIDE_STATIONS].map(([id, s]) => ({
          id, name: s.n, lat: s.y, lon: s.x, subordinate: s.s === true,
        })),
        TIDE_STATIONS,
      );
      return tideCache;
    })
    .catch((err) => {
      tideInFlight = null;
      throw err;
    });

  return tideInFlight;
}

/** The national current stations, loaded once. */
export function nationalCurrentStations(): Promise<CoopsPoint[]> {
  if (currentCache) return Promise.resolve(currentCache);
  if (currentInFlight) return currentInFlight;

  currentInFlight = import('../nationalCurrentStations.ts')
    .then((m) => {
      currentCache = withoutCurated(
        [...m.NATIONAL_CURRENT_STATIONS].map(([id, s]) => ({ id, name: s.n, lat: s.y, lon: s.x })),
        CURRENT_STATIONS,
      );
      return currentCache;
    })
    .catch((err) => {
      currentInFlight = null;
      throw err;
    });

  return currentInFlight;
}

/** Stations inside the given bounds. */
export function withinBounds(stations: readonly CoopsPoint[], b: Bounds): CoopsPoint[] {
  return stations.filter(
    (s) => s.lat <= b.north && s.lat >= b.south && s.lon <= b.east && s.lon >= b.west,
  );
}

/**
 * Trims a view to a drawable number of markers, nearest the middle first.
 *
 * A cap alone would keep whichever stations happen to sort first, which on a
 * zoomed-out view means an arbitrary corner of the country is dense and the rest
 * is bare. Keeping the ones nearest the centre puts the markers where the reader
 * is actually looking.
 */
export function nearestFirst(stations: CoopsPoint[], b: Bounds, max: number): CoopsPoint[] {
  if (stations.length <= max) return stations;

  const cy = (b.north + b.south) / 2;
  const cx = (b.east + b.west) / 2;
  const d2 = (s: CoopsPoint) => (s.lat - cy) ** 2 + (s.lon - cx) ** 2;

  return [...stations].sort((p, q) => d2(p) - d2(q)).slice(0, max);
}
